import PropTypes from 'prop-types';
import React from 'react';
import DataSourceLogo from './DataSourceLogo';
import Icon from 'components/Icon';
import {connect} from 'react-redux';
import {
  selectDataSourceById,
  selectFilterGroups,
} from 'state/dataSource/dataSourceSelectors';
import {randomHash} from 'utilities/hash';
import './DataSourceFilters.css';
import {PredicateEditor, FilterGroupEditor} from 'components/FilterEditor';
import FilterList, {FilterListGroupItem} from 'components/FilterList';
import Circle from 'components/activity/Circle';
import constantObject from 'utilities/constantObject';
import {injectIntl, defineMessages, FormattedMessage} from 'react-intl';
import cx from 'classnames';
import ButtonUnstyled from 'components/ButtonUnstyled';

const EDITOR_STATES = constantObject(['NONE', 'FILTER', 'FILTER_GROUP']);

const MESSAGES = defineMessages({
  dataset: {
    defaultMessage: 'See dataset name',
    id: 'DataSourceFilters__DatasetLabel',
    description: 'Label for icon that toggles the display of the dataset name',
  },
  filter: {
    defaultMessage: 'Add a new filter or modify an existing one',
    id: 'DataSourceFilters__FilterLabel',
    description:
      'Label for icon that toggles the display of the selected filter',
  },
});

const LoadingDataSourceFilters = () => (
  <div className="DataSourceFilters">
    <div className="DataSourceFilters__List">
      <div className="DataSourceFilters__Logo">
        <Circle size={Circle.sizes.SMALL} />
      </div>
      <div className="DataSourceFilters__ListPlaceholder" />
    </div>
  </div>
);

const toggleDetails = state => ({
  detailsOpen: !state.detailsOpen,
});

const deselectFilter = () => ({
  selectedFilterId: null,
  editorState: EDITOR_STATES.NONE,
});

class DataSourceFilters extends React.Component {
  state = {
    editorState: EDITOR_STATES.NONE,
    selectedFilterId: null,
    detailsOpen: false,
  };

  handleAddClick = () => {
    const selectedFilterId = randomHash();
    this.setState({
      selectedFilterId,
      editorState: EDITOR_STATES.FILTER,
    });
    this.props.onAdd(selectedFilterId);
  };

  handleFilterSelect = filter => {
    const selectedFilterId =
      this.state.selectedFilterId === filter.id ? null : filter.id;

    this.setState({
      selectedFilterId,
      editorState: selectedFilterId ? EDITOR_STATES.FILTER : EDITOR_STATES.NONE,
    });
  };

  handleFilterGroupSelect = () => {
    const {editorState} = this.state;

    this.setState({
      selectedFilterId: null,
      editorState:
        editorState === EDITOR_STATES.FILTER_GROUP
          ? EDITOR_STATES.NONE
          : EDITOR_STATES.FILTER_GROUP,
    });
  };

  handleApply = filter => {
    this.setState(deselectFilter);
    this.props.onApply(filter);
  };

  handleRemove = () => {
    this.setState(deselectFilter);
    this.props.onRemove(this.state.selectedFilterId);
  };

  handleFilterGroupApply = filterGroupIds => {
    this.setState(deselectFilter);
    this.props.onFilterGroupApply(filterGroupIds);
  };

  handleDetailsToggle = () => {
    this.setState(toggleDetails);
    this.setState(deselectFilter);
  };

  render() {
    const {
      canSelectFilterGroups,
      dataSource,
      filterGroups,
      filters,
      pageId,
      cardId,
      intl,
    } = this.props;
    const {editorState, detailsOpen} = this.state;

    // Treat no selection as "All"
    const selectedFilterGroupIds =
      this.props.selectedFilterGroupIds.length > 0
        ? this.props.selectedFilterGroupIds
        : filterGroups.map(f => f.filterGroupId);

    if (!dataSource) return <LoadingDataSourceFilters />;

    const selectedFilter = filters.find(
      f => f.id === this.state.selectedFilterId
    );
    const hasInactiveFilter = filters.some(f => !f.applied);
    const hasFilterGroups = filterGroups.length > 1;
    const filterCount = filters.length + (hasFilterGroups ? 1 : 0);

    return (
      <div className="DataSourceFilters">
        <div className="DataSourceFilters__List">
          <div
            onClick={this.handleDetailsToggle}
            className={cx({
              DataSourceFilters__Details: true,
              'DataSourceFilters__Details--open': detailsOpen,
            })}
          >
            <ButtonUnstyled
              className="DataSourceFilters__Logo"
              onClick={this.handleLogoClick}
              title={intl.formatMessage(MESSAGES.dataset)}
            >
              <DataSourceLogo
                className="DataSourceFilters__LogoImage"
                provider={dataSource.type}
                size={DataSourceLogo.SIZES.MEDIUM}
              />
            </ButtonUnstyled>
            <div className="DataSourceFilters__Title">
              <div className="DataSourceFilters__Label">
                <FormattedMessage
                  id="DataSourceFilters__Label"
                  description="Description label for DataSets in analyzer"
                  defaultMessage="DataSet"
                />
              </div>
              <div className="DataSourceFilters__Name">{dataSource.name}</div>
            </div>
          </div>
          {!hasInactiveFilter && !detailsOpen ? (
            <ButtonUnstyled
              className="DataSourceFilters__Add"
              onClick={this.handleAddClick}
              title={intl.formatMessage(MESSAGES.filter)}
            >
              <Icon icon={Icon.OPTIONS.FUNNEL_PLUS} />
            </ButtonUnstyled>
          ) : null}
          {detailsOpen ? (
            <div className="DataSourceFilters__Count">
              <ButtonUnstyled
                onClick={this.handleDetailsToggle}
                title={intl.formatMessage(MESSAGES.filter)}
              >
                <Icon icon={Icon.OPTIONS.FUNNEL} size={Icon.SIZES.PICO} />
                {filterCount}
              </ButtonUnstyled>
            </div>
          ) : null}

          <FilterList
            filters={filters}
            selectedFilter={selectedFilter}
            onFilterSelect={this.handleFilterSelect}
          >
            {canSelectFilterGroups && filterGroups.length > 1 ? (
              <FilterListGroupItem
                selected={editorState === EDITOR_STATES.FILTER_GROUP}
                filterGroups={filterGroups}
                selectedFilterGroupIds={selectedFilterGroupIds}
                onClick={this.handleFilterGroupSelect}
              />
            ) : null}
          </FilterList>
        </div>
        {selectedFilter && editorState === EDITOR_STATES.FILTER ? (
          <PredicateEditor
            cardId={cardId}
            dataSourceIds={[dataSource.id]}
            filter={selectedFilter}
            onApply={this.handleApply}
            onRemove={this.handleRemove}
            pageId={pageId}
          />
        ) : null}
        {editorState === EDITOR_STATES.FILTER_GROUP ? (
          <FilterGroupEditor
            filterGroups={filterGroups}
            selectedFilterGroupIds={selectedFilterGroupIds}
            onApply={this.handleFilterGroupApply}
          />
        ) : null}
      </div>
    );
  }
}

DataSourceFilters.propTypes = {
  dataSourceId: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onFilterGroupApply: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  pageId: PropTypes.string,
  cardId: PropTypes.string,
  canSelectFilterGroups: PropTypes.bool.isRequired,
  selectedFilterGroupIds: PropTypes.array.isRequired,
};

DataSourceFilters.defaultProps = {
  canSelectFilterGroups: false,
  selectedFilterGroupIds: [],
};

export default injectIntl(
  connect((state, {dataSourceId}) => ({
    dataSource: selectDataSourceById(state, dataSourceId),
    filterGroups: selectFilterGroups(state, dataSourceId),
  }))(DataSourceFilters)
);
